import BridgeSocket, { BridgeSocketEvents } from '@flipster/socket/BridgeSocket';
import { V2SocketEvents } from '@flipster/socket/r230522/socket';
import type { WsV2Message } from '@flipster/socket/r230522/types';
import { tradeLogger } from '@flipster/utils/datadog';
import segment from '@flipster/utils/segment';
import { PerformanceEvent } from '@flipster/utils/segment/events/performance';

export interface BridgeR230522Events
  extends BridgeSocketEvents<WsV2Message, unknown>,
    Omit<V2SocketEvents, 'message'> {}
export const bridgeR230522Socket = new BridgeSocket<WsV2Message, unknown, unknown, unknown, BridgeR230522Events>(
  'socket:r230522',
  {},
);

type StopFn = () => void;

export function startBridgeConnectionErrorLogging(isAllowedError: (error: Error) => boolean = () => false): StopFn {
  let connectionFailureCount = 0;
  const offConnected = bridgeR230522Socket.on('connected', () => void (connectionFailureCount = 0));
  const offConnectionError = bridgeR230522Socket.on('connectionError', (error: Error) => {
    if (isAllowedError(error)) return;
    connectionFailureCount++;
    if (connectionFailureCount !== 10) return;
    tradeLogger.error(
      'webview_bridge_not_connected',
      {
        error_category: 'network_errors',
        error_message: error.message,
      },
      error,
    );
  });
  return () => {
    offConnected();
    offConnectionError();
  };
}

export function startBridgeConnectionDurationLogging(): StopFn {
  let connectionStartTime: number | null = null;
  const offStateChanged = bridgeR230522Socket.on('stateChanged', (state) => {
    switch (state) {
      case 'connecting': {
        connectionStartTime = performance.now();
        break;
      }
      case 'closed': {
        connectionStartTime = null;
        break;
      }
      case 'connected': {
        if (!connectionStartTime) break;
        const now = performance.now();
        const durationNanoseconds = (now - connectionStartTime) * 1_000_000;
        const params = {
          cpe_name: 'webview_bridge_connected',
          domain: 'trade',
          log_version: 1,
          duration: durationNanoseconds,
        } satisfies PerformanceEvent['perf_webview_bridge_connected'];
        tradeLogger.info('webview_bridge_connected', params);
        segment.track('perf_webview_bridge_connected', params);
        break;
      }
    }
  });
  return () => {
    offStateChanged();
  };
}
